<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  import Input from '$lib/components/ui/input/input.svelte';
  import Table from '../../ui/table/table.svelte';
  import TableHeader from '../../ui/table/table-header.svelte';
  import TableRow from '../../ui/table/table-row.svelte';
  import TableHead from '../../ui/table/table-head.svelte';
  import TableCell from '../../ui/table/table-cell.svelte';
  import TableBody from '../../ui/table/table-body.svelte';
  import { Pencil, Trash2, ChevronDown, ChevronUp, Search, ChevronLeft, ChevronRight } from 'lucide-svelte';
  import { toast } from 'svelte-sonner';
  import { designsMockData } from './viewdesigns.mock';
  import type { Design, DesignFilters } from './viewdesigns.types';

  let {
    open = $bindable(false),
    title = 'Designs',
    size = 'sm:max-w-[1200px]',
    designs = $bindable<Design[]>(designsMockData),
    experience = null as any,
    onLoadDesign,
    onDeleteDesign,
  }: {
    open?: boolean;
    title?: string;
    size?: string;
    designs?: Design[];
    experience?: any;
    onLoadDesign?: (design: Design) => void;
    onDeleteDesign?: (designId: string) => void;
  } = $props();

  let loading = $state(false);
  let searchQuery = $state('');
  let sortBy = $state<'name' | 'createdAt' | 'updatedAt'>('updatedAt');
  let sortOrder = $state<'asc' | 'desc'>('desc');
  let currentPage = $state(1);
  let itemsPerPage = $state(7);

  // Fetch designs from experience when modal opens
  $effect(() => {
    if (open && experience?.dataVault) {
      loadDesigns();
    }
  });

  async function loadDesigns() {
    loading = true;
    try {
      await experience.dataVault.fetchDesigns();
      const fetchedDesigns = experience.dataVault.designs || [];
      designs = fetchedDesigns;
    } catch (error) {
      console.error('Failed to load designs:', error);
      toast.error('Failed to load designs');
    } finally {
      loading = false;
    }
  }

  // Filtered and sorted designs
  let filteredDesigns = $derived.by(() => {
    let result = [...(designs || [])];

    // Filter by search query
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase();
      result = result.filter(
        (design) =>
          design.name.toLowerCase().includes(term) ||
          design.description?.toLowerCase().includes(term) ||
          design.note?.toLowerCase().includes(term),
      );
    }

    // Sort designs
    result.sort((a, b) => {
      let compareValue = 0;

      if (sortBy === 'name') {
        compareValue = a.name.localeCompare(b.name);
      } else if (sortBy === 'createdAt') {
        compareValue =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else if (sortBy === 'updatedAt') {
        compareValue =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return result;
  });

  // Paginated designs
  let paginatedDesigns = $derived.by(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDesigns.slice(startIndex, endIndex);
  });

  // Total pages
  let totalPages = $derived(Math.ceil(filteredDesigns.length / itemsPerPage));

  // Page numbers to display
  let pageNumbers = $derived.by(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }

    return pages;
  });

  function handleEditDesign(design: Design) {
    if (!experience?.eventHub) {
      toast.error('Unable to load design: experience not available');
      return;
    }

    // Emit viewProject event with design data
    experience.eventHub.emit('viewProject', {
      uuid: design.uuid || design.id,
      name: design.name,
      content: design.content,
    });

    toast.success(`Loading design: ${design.name}`);
    open = false;
  }

  function handleDeleteDesign(design: Design) {
    if (!experience?.eventHub) {
      toast.error('Unable to delete design: experience not available');
      return;
    }

    // Close this modal first to allow confirmation dialog to be interactive
    open = false;

    // Show confirmation modal after a brief delay to ensure this modal is closed
    setTimeout(() => {
      experience.eventHub.emit('modalInfo', {
        title: 'Delete Design',
        message: `Are you sure you want to delete "${design.name}"? This action cannot be undone.`,
        actions: [
          {
            label: 'Cancel',
            callback: () => {
              // Reopen the designs modal
              open = true;
            },
          },
          {
            label: 'Delete',
            callback: async () => {
              await performDelete(design);
              // Reopen the designs modal after deletion
              open = true;
            },
          },
        ],
        autoClose: false,
      });
    }, 100);
  }

  async function performDelete(design: Design) {
    loading = true;
    try {
      const designId = design.uuid || design.id;

      // Call API to delete design
      if (experience?.dataProvider) {
        await experience.dataProvider.delete('design', designId);
      }

      // Remove from local array
      designs = designs.filter((d) => d.id !== design.id && d.uuid !== design.uuid);

      toast.success(`Design "${design.name}" deleted successfully`);

      // Refresh designs list
      await loadDesigns();
    } catch (error) {
      console.error('Failed to delete design:', error);
      toast.error('Failed to delete design');
    } finally {
      loading = false;
    }
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD like Figma
  }

  function toggleSort(column: 'name' | 'createdAt' | 'updatedAt') {
    if (sortBy === column) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortBy = column;
      sortOrder = 'desc';
    }
  }

  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages) {
      currentPage = page;
    }
  }

  function nextPage() {
    if (currentPage < totalPages) {
      currentPage++;
    }
  }

  function prevPage() {
    if (currentPage > 1) {
      currentPage--;
    }
  }
</script>

<Dialog.Root bind:open preventScroll={false}>
  <Dialog.Content class={`${size} border-none shadow-lg !bg-white dark:!bg-[var(--theme-main)] dark:!text-[var(--theme-font)] !max-h-[90vh]`}>
    <Dialog.Header class="!p-6 !pb-4">
      <Dialog.Title class="text-2xl font-bold">{title}</Dialog.Title>
    </Dialog.Header>

    <div class="flex !flex-col gap-0">
      <!-- Search Bar -->
      <div class="flex items-center gap-3 w-full !px-6 !pb-4">
        <div class="relative" style="width: 400px;">
          <Search class="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            bind:value={searchQuery}
            placeholder="Search designs..."
            class="!pl-12 !pr-4 !py-4 !h-14 border-2 border-gray-300 dark:border-gray-600 rounded-lg !text-lg !font-normal !text-gray-900 dark:!text-[var(--theme-font)] placeholder:!text-gray-500 dark:placeholder:!text-gray-400 !bg-white dark:!bg-[var(--theme-main)]"
            style="width: 400px !important; min-width: 400px !important;"
          />
        </div>
      </div>

      <!-- Designs Table -->
      <div class="w-full bg-white dark:bg-[var(--theme-main)] overflow-hidden">
        <div class="overflow-x-auto max-h-[500px]">
          <Table class="text-sm min-w-full">
            <TableHeader class="sticky top-0 z-10 bg-white dark:bg-[var(--theme-main)] border-y border-gray-200 dark:border-gray-600">
              <TableRow class="hover:bg-transparent">
                <TableHead
                  class="!py-3 !px-4 font-medium text-gray-700 dark:text-[var(--theme-font)] cursor-pointer hover:bg-gray-50 dark:hover:bg-[var(--theme-canvas)]"
                  onclick={() => toggleSort('name')}
                >
                  <div class="flex items-center gap-2">
                    Name
                    {#if sortBy === 'name'}
                      {#if sortOrder === 'asc'}
                        <ChevronUp class="w-4 h-4" />
                      {:else}
                        <ChevronDown class="w-4 h-4" />
                      {/if}
                    {:else}
                      <ChevronDown class="w-4 h-4 opacity-30" />
                    {/if}
                  </div>
                </TableHead>
                <TableHead
                  class="!py-3 !px-4 font-medium text-gray-700 dark:text-[var(--theme-font)] cursor-pointer hover:bg-gray-50 dark:hover:bg-[var(--theme-canvas)]"
                  onclick={() => toggleSort('createdAt')}
                >
                  <div class="flex items-center gap-2">
                    Created at
                    {#if sortBy === 'createdAt'}
                      {#if sortOrder === 'asc'}
                        <ChevronUp class="w-4 h-4" />
                      {:else}
                        <ChevronDown class="w-4 h-4" />
                      {/if}
                    {:else}
                      <ChevronDown class="w-4 h-4 opacity-30" />
                    {/if}
                  </div>
                </TableHead>
                <TableHead
                  class="!py-3 !px-4 font-medium text-gray-700 dark:text-[var(--theme-font)] cursor-pointer hover:bg-gray-50 dark:hover:bg-[var(--theme-canvas)]"
                  onclick={() => toggleSort('updatedAt')}
                >
                  <div class="flex items-center gap-2">
                    Updated at
                    {#if sortBy === 'updatedAt'}
                      {#if sortOrder === 'asc'}
                        <ChevronUp class="w-4 h-4" />
                      {:else}
                        <ChevronDown class="w-4 h-4" />
                      {/if}
                    {:else}
                      <ChevronDown class="w-4 h-4 opacity-30" />
                    {/if}
                  </div>
                </TableHead>
                <TableHead class="!py-3 !px-6 font-medium text-gray-700 dark:text-[var(--theme-font)] text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {#if loading}
                <TableRow>
                  <TableCell colspan={4} class="!py-12 text-center text-gray-500">
                    Loading designs...
                  </TableCell>
                </TableRow>
              {:else if paginatedDesigns.length > 0}
                {#each paginatedDesigns as design (design.uuid || design.id)}
                  <TableRow class="hover:bg-gray-50 dark:hover:bg-[var(--theme-canvas)] border-b border-gray-100 dark:border-gray-600">
                    <TableCell class="!py-4 !px-4 font-medium text-gray-900 dark:text-[var(--theme-font-alt)]">
                      <span class="max-w-[200px] truncate block">{design.name}</span>
                    </TableCell>
                    <TableCell class="!py-4 !px-4 text-gray-600 dark:text-[var(--theme-font)]">
                      {formatDate(design.createdAt)}
                    </TableCell>
                    <TableCell class="!py-4 !px-4 text-gray-600 dark:text-[var(--theme-font)]">
                      {formatDate(design.updatedAt)}
                    </TableCell>
                    <TableCell class="!py-4 !px-6">
                      <div class="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          class="w-8 h-8 flex items-center justify-center rounded bg-[#004976] dark:bg-[var(--theme-link)] hover:brightness-110 text-white transition"
                          onclick={() => handleEditDesign(design)}
                          aria-label="Edit design"
                          title="Edit design"
                        >
                          <Pencil class="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          class="w-8 h-8 flex items-center justify-center rounded bg-gray-200 dark:bg-[var(--theme-canvas)] hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-[var(--theme-font)] transition"
                          onclick={() => handleDeleteDesign(design)}
                          aria-label="Delete design"
                          title="Delete design"
                        >
                          <Trash2 class="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                {/each}
              {:else}
                <TableRow>
                  <TableCell colspan={4} class="!py-12 text-center text-gray-500">
                    {searchQuery.trim()
                      ? 'No designs found matching your search.'
                      : 'No designs found. Start by creating a new design!'}
                  </TableCell>
                </TableRow>
              {/if}
            </TableBody>
          </Table>
        </div>
      </div>

      <!-- Pagination -->
      {#if !loading && filteredDesigns.length > 0}
        <div class="flex items-center justify-between !px-6 !py-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-[var(--theme-font)] hover:text-gray-900 dark:hover:text-[var(--theme-font-alt)] disabled:opacity-50 disabled:cursor-not-allowed"
            onclick={prevPage}
            disabled={currentPage === 1}
          >
            <ChevronLeft class="w-4 h-4" />
            PREV
          </button>

          <div class="flex items-center gap-2">
            {#each pageNumbers as page}
              {#if page === '...'}
                <span class="px-3 py-1 text-sm text-gray-500 dark:text-[var(--theme-font)]">...</span>
              {:else}
                <button
                  type="button"
                  class="px-3 py-1 text-sm rounded {currentPage === page
                    ? 'font-bold text-gray-900 dark:text-[var(--theme-font-alt)] bg-gray-100 dark:bg-[var(--theme-canvas)]'
                    : 'text-gray-600 dark:text-[var(--theme-font)] hover:bg-gray-50 dark:hover:bg-[var(--theme-canvas)]'}"
                  onclick={() => goToPage(page as number)}
                >
                  {page}
                </button>
              {/if}
            {/each}
          </div>

          <button
            type="button"
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-[var(--theme-font)] hover:text-gray-900 dark:hover:text-[var(--theme-font-alt)] disabled:opacity-50 disabled:cursor-not-allowed"
            onclick={nextPage}
            disabled={currentPage === totalPages}
          >
            NEXT
            <ChevronRight class="w-4 h-4" />
          </button>
        </div>
      {/if}
    </div>
  </Dialog.Content>
</Dialog.Root>
